import type { Exibicao } from '../workspace/memoria';
import type { ItemDeChat } from './mensagens';

/**
 * O que cada chave de `Exibicao` esconde na conversa.
 *
 * Mora aqui, e não espalhado num `if` dentro do componente, porque a Prosa
 * precisa da MESMA regra com padrões diferentes — e porque contar o que foi
 * escondido só é honesto se quem conta e quem filtra usam a mesma tabela. Duas
 * listas escritas à mão divergem; foi assim que `assemblyai` sobreviveu meses
 * num enum sem implementação.
 *
 * `diffAberto` e `telaAcesa` não entram: não escondem item nenhum.
 */
const GOVERNADO: Partial<Record<ItemDeChat['tipo'], keyof Exibicao>> = {
  ferramenta: 'ferramentas',
  pensamento: 'pensamento',
  tarefas: 'tarefas',
};

/** O item aparece com esta exibição? */
export function itemVisivel(item: ItemDeChat, exibicao: Exibicao): boolean {
  /*
    ★ Imagem e arquivo não são comando.

    "Esconder comandos e resultados" é o pedido — e na Prosa é o padrão
    (`EXIBICAO_PROSA.ferramentas === false`). Esconder junto a IMAGEM que a
    sessão leu é dano colateral: a regra da casa manda a conversa mostrar
    imagem como imagem, e não abre exceção por tela. O dono pediu exatamente
    isso: "quando fizer um Read de uma imagem, eu quero visualizar essa imagem".

    O que continua escondido é o cartão fechado com a entrada crua — o que
    aparece a mais é a miniatura, que é o ponto.
  */
  if (item.tipo === 'ferramenta' && (item.imagem || item.anexo)) return true;

  const chave = GOVERNADO[item.tipo];
  // Tipo que ninguém governa aparece sempre. É o padrão certo: item novo no
  // parser nasce VISÍVEL, e quem quiser esconder declara. O contrário faria
  // uma conversa emudecer sozinha ao ganhar um tipo de bloco novo.
  return chave === undefined ? true : exibicao[chave];
}

/** A conversa filtrada, na ordem em que veio. */
export function filtrarPorExibicao(
  itens: readonly ItemDeChat[],
  exibicao: Exibicao,
): ItemDeChat[] {
  return itens.filter((item) => itemVisivel(item, exibicao));
}

/**
 * Quantos itens a exibição está escondendo, por tipo.
 *
 * É o que sustenta a marca de elisão: esconder sem dizer o quanto é a
 * armadilha que a gente passou dois dias caçando — informação que some sem
 * aviso. Devolve mapa vazio quando nada está escondido, para a tela não
 * desenhar chip nenhum.
 */
export function contarEscondidos(
  itens: readonly ItemDeChat[],
  exibicao: Exibicao,
): Partial<Record<ItemDeChat['tipo'], number>> {
  const conta: Partial<Record<ItemDeChat['tipo'], number>> = {};
  for (const item of itens) {
    if (itemVisivel(item, exibicao)) continue;
    conta[item.tipo] = (conta[item.tipo] ?? 0) + 1;
  }
  return conta;
}

/** Só o total, para o chip que diz "N escondidos". */
export function totalEscondido(itens: readonly ItemDeChat[], exibicao: Exibicao): number {
  return itens.reduce((total, item) => (itemVisivel(item, exibicao) ? total : total + 1), 0);
}

/** As chaves que, se ligadas, revelam o que está escondido agora. */
export function chavesQueRevelam(
  itens: readonly ItemDeChat[],
  exibicao: Exibicao,
): (keyof Exibicao)[] {
  const chaves = new Set<keyof Exibicao>();
  for (const item of itens) {
    if (itemVisivel(item, exibicao)) continue;
    const chave = GOVERNADO[item.tipo];
    if (chave) chaves.add(chave);
  }
  return [...chaves];
}
